import React, { useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

import { CreateUserData } from '@/types';

interface CreateUserModalProps {
  onClose: () => void;
  onCreate: (userData: CreateUserData) => Promise<boolean>;
}

/**
 * Theme-aware modal component for creating new users
 */
const CreateUserModal: React.FC<CreateUserModalProps> = ({ onClose, onCreate }) => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'athlete' as 'admin' | 'coach' | 'athlete',
    first_name: '',
    last_name: '',
    team_code: ''
  });
  const [codeStatus, setCodeStatus] = useState<'idle' | 'loading' | 'valid' | 'invalid'>('idle');
  const [resolvedRole, setResolvedRole] = useState<'coach' | 'athlete' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!formData.username || !formData.email || !formData.password || !formData.first_name || !formData.last_name) {
      setError('Please fill in all required fields');
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    // team_code optional

    try {
      setLoading(true);
      const ok = await onCreate({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: resolvedRole ?? formData.role,
        first_name: formData.first_name,
        last_name: formData.last_name,
        team_code: formData.team_code || undefined
      } as CreateUserData);
      if (ok) {
        setSuccess('User created successfully.');
        setTimeout(() => onClose(), 1200);
      } else {
        setError('Failed to create user. Please try again.');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      setError(error instanceof Error ? error.message : 'An error occurred while creating the user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
         <div className="fixed inset-0 bg-gray-800/80 dark:bg-gray-950/80 overflow-y-auto h-full w-full z-50">
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <h3 className="text-2xl cgm-text-primary mb-2">
              Create New User
            </h3>
            <p className="cgm-text-secondary text-sm">
              Add a new user to the system
            </p>
          </div>
          {error && (
            <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">{error}</div>
          )}
          {success && (
            <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">{success}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium cgm-text-secondary mb-1">
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  disabled={loading}
                  className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="First name"
                />
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium cgm-text-secondary mb-1">
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  disabled={loading}
                  className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="Last name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium cgm-text-secondary mb-1">
                Username
              </label>
              <input
                id="username"
                type="text"
                required
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter username"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium cgm-text-secondary mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium cgm-text-secondary mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Enter password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium cgm-text-secondary mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Re-enter password"
              />
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium cgm-text-secondary mb-1">
                Role
              </label>
              <select
                id="role"
                value={resolvedRole ?? formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value as 'admin' | 'coach' | 'athlete'})}
                disabled={loading || codeStatus === 'valid'}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="athlete">Athlete</option>
                <option value="coach">Coach</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="relative">
              <label htmlFor="teamCode" className="block text-sm font-medium cgm-text-secondary mb-1">
                Athlete/Coach Code (optional)
              </label>
              <input
                id="teamCode"
                type="text"
                value={formData.team_code}
                onChange={async (e) => {
                  const value = e.target.value;
                  setFormData({...formData, team_code: value});
                  setResolvedRole(null);
                  if (!value) { setCodeStatus('idle'); return; }
                  setCodeStatus('loading');
                  try {
                    const token = localStorage.getItem('access');
                    const res = await fetch(`/api/teams/admin/validate-team-code/?code=${encodeURIComponent(value)}`, {
                      headers: token ? { 'Authorization': `Bearer ${token}` } : undefined
                    });
                    const data = await res.json();
                    if (data.valid && (data.role === 'coach' || data.role === 'athlete')) {
                      setResolvedRole(data.role);
                      setCodeStatus('valid');
                    } else {
                      setResolvedRole(null);
                      setCodeStatus('invalid');
                    }
                  } catch (err) {
                    setResolvedRole(null);
                    setCodeStatus('invalid');
                  }
                }}
                disabled={loading}
                className="cgm-input w-full px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder={'Enter join code (optional)'}
              />
              <div className="absolute right-3 top-11.5 -translate-y-1/2 flex items-center pointer-events-none">
                {codeStatus === 'loading' && (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-gray-500"></div>
                )}
                {codeStatus === 'valid' && (
                  <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {codeStatus === 'invalid' && (
                  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || (formData.team_code ? codeStatus !== 'valid' : false)}
              className="cgm-button w-full mt-6 py-3 px-4 flex items-center justify-center"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                  Creating User...
                </>
              ) : (
                'Create User'
              )}
            </button>
          </form>

          {/* Navigation Links */}
          <div className="mt-6 space-y-4 text-center">
                         <button
               onClick={onClose}
               className="cgm-cancel-button w-full py-3 px-4 flex items-center justify-center rounded-lg font-medium"
             >
               Cancel
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateUserModal; 